import { Card } from '@zorro/design-system/v2/Card';
import { Link } from '@zorro/design-system/v2/Link';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import { Box, Center, Group, Stack, Text } from '@zorro/web/design-system';
import {
  BenefitTypeBadge,
  CarrierLogoNext,
  MajorMedicalPlanCardSkeleton,
  PolicyTypeBadge,
  RenewalBadge,
  SelfEnrollBadge,
  useAnalytics,
} from '@zorro/web/shared-ui';
import { useMonolithQuery } from '@zorro/web/ui-utils';

type Props = {
  onboardingPeriodId: string;
  benefitId?: string;
};

export const ElectedPlanCard = ({ onboardingPeriodId, benefitId }: Props) => {
  const { sendEvent } = useAnalytics();

  const { data: benefits, isLoading: isLoadingBenefits } = useMonolithQuery({
    method: 'benefitsControllerGetBenefits',
    params: [onboardingPeriodId],
  });
  const { data: onboardingPeriodStatuses, isLoading: isLoadingStatuses } =
    useMonolithQuery({
      method: 'enrollmentReportsControllerFetchSingleOnboardingPeriodStatuses',
      params: [onboardingPeriodId],
    });

  if (isLoadingBenefits || isLoadingStatuses) {
    return <MajorMedicalPlanCardSkeleton />;
  }

  const benefit = benefits?.allBenefits.find(
    (benefit) => benefit.id === benefitId
  );

  return (
    <Group align="flex-start" gap="md" wrap="nowrap">
      <Card variant="white-shadow" radius="xs" w="7.5rem" h="3.75rem" p={0}>
        <Center w="100%" h="100%">
          <CarrierLogoNext
            plan={{ carrierName: benefit?.plan?.carrierName || '' }}
            width={96}
            height={32}
            style={{ margin: 0 }}
          />
        </Center>
      </Card>
      <Stack gap="xs" flex={1} miw={0}>
        <Tooltip
          label={benefit?.plan?.name ?? ''}
          disabled={!benefit?.plan?.name}
          position="top-start"
        >
          <Box w="100%">
            {benefit?.plan?.benefitsSummaryUrl ? (
              <Link
                href={benefit.plan.benefitsSummaryUrl}
                target="_blank"
                variant="primary"
                size="md"
                c="var(--color-text-default-primary)"
                shouldTruncate
                onClick={() =>
                  sendEvent('click_plan_details', {
                    onboarding_period_id: onboardingPeriodId,
                    carrier_name: benefit?.plan?.carrierName,
                    plan_name: benefit?.plan?.name,
                    plan_id: benefit?.plan?.externalID,
                  })
                }
              >
                {benefit?.plan?.name}
              </Link>
            ) : (
              <Text size="md" truncate>
                {benefit?.plan?.name}
              </Text>
            )}
          </Box>
        </Tooltip>
        <Group gap="xs">
          <BenefitTypeBadge benefitType={benefit?.type} />
          <PolicyTypeBadge policyType={benefit?.plan?.policyType} />
          <RenewalBadge
            coverageChange={onboardingPeriodStatuses?.coverageChange}
          />
          <SelfEnrollBadge selfEnroll={benefit?.plan?.selfEnroll} />
        </Group>
        {benefit?.plan?.isCombinedPlan &&
          benefit?.plan?.extendedPlanDetails && (
            <Text size="sm">
              <Text span fw={600} size="sm" mr="xxs">
                Combined plan info:
              </Text>
              {benefit.plan.extendedPlanDetails}
            </Text>
          )}
      </Stack>
    </Group>
  );
};
